import type { FC } from "react";
import { useSearchParams } from "react-router-dom";
import type { GridCard } from "widgets";
import { Card, CardRowSimple, formatDateTimeFromISO, Tag, TagArrow, TagIntent } from "widgets";

import { isSyntenySet } from "@/app-utils/entity.ts";
import type { Organism, UserGroup } from "@/graphql.js";
import { GENOME_TYPE_REFERENCE, UNKNOWN_GENOME_NAME } from "@/settings/settings.js";
import { SyntenyViewTile } from "@/ui-modules/core/tiles/components/SyntenyViewTile.tsx";

import style from "../../core/cards/styles/Card.module.scss";
import { GenomeTile } from "../../core/tiles/index.js";

export interface IGenomeCardProps extends GridCard {
  title?: string;
  genome: Organism;
  isEntitySummary?: boolean;
  isSampleContext?: boolean;
}

const GenomeCard: FC<IGenomeCardProps> = (props) => {
  const [searchParams, _] = useSearchParams();
  if (!props.genome || props.genome.name === UNKNOWN_GENOME_NAME) {
    return null;
  }

  const genome: Organism = props.genome;
  const referenceTrailer = genome.type === GENOME_TYPE_REFERENCE ? ` (${GENOME_TYPE_REFERENCE})` : null;

  const viewid = searchParams.get("viewid");

  return (
    <Card key="genome-info-card" title={props.title ?? "Genome Info"}>
      {props.isEntitySummary ? (
        <CardRowSimple label={"Name"}>{genome.name}</CardRowSimple>
      ) : (
        <CardRowSimple label={"Name"}>
          <GenomeTile genome={genome} />
        </CardRowSimple>
      )}
      {!props.genome.hidden && (
        <>
          <CardRowSimple label={"Species"} value={<em>{genome.species.name}</em>} />
          {genome.species.synonym && (
            <CardRowSimple label={"Species synonyms"}>
              <div className={style.tags_list}>
                {genome.species.synonym.split(";").map((syn) => (
                  <Tag key={syn} label={syn.trim()} intentColor={TagIntent.Secondary} />
                ))}
              </div>
            </CardRowSimple>
          )}
          <CardRowSimple label={"Strain"} value={genome.strain} />
          <CardRowSimple label={"Common name"} value={genome.species.commonName} />
          {(props.isEntitySummary || props.isSampleContext) && (
            <CardRowSimple label={"Custom ID"} value={genome.customId} />
          )}
          {(props.isEntitySummary || props.isSampleContext) && (
            <CardRowSimple label={"Description"} value={genome.description} />
          )}
          {(props.isEntitySummary || props.isSampleContext) && (
            <CardRowSimple label={"Identifier"} value={genome.identifier} />
          )}
          <CardRowSimple label={"Taxonomy group"} value={genome.taxonomy} />
          {genome.species.lineage && (
            <CardRowSimple label={"Lineage"}>
              <div className={style.tags_list}>
                {genome.species.lineage.split(";").map((lin) => (
                  <TagArrow key={`tag-arrow-${lin}`} label={lin.trim()} />
                ))}
              </div>
            </CardRowSimple>
          )}
          <CardRowSimple label={"Genome type"} value={genome.sequencingType} />
          <CardRowSimple label={"Genotype description"} value={genome.genotypeDescription} />
          {genome.variantAnalysisGroups?.length > 0 && (
            <CardRowSimple
              label={"Genome analysis group"}
              value={genome.variantAnalysisGroups
                .map((analysisGroup) => `${analysisGroup.name}${referenceTrailer ?? ""}`)
                .join(", ")}
            />
          )}
          <CardRowSimple label={"Genome version"} value={genome.version} />
          {genome.databases && (
            <CardRowSimple
              label={"Data sources"}
              value={genome.databases.map((db) => `${db.name} (${db.mnemonic})`).join(", ")}
            />
          )}
          <CardRowSimple label={"Organization"} value={genome.organisation && genome.organisation.name} />
          {genome.created && (
            <CardRowSimple label={"Creation date"} value={formatDateTimeFromISO(genome.created, { hideTime: true })} />
          )}
          {genome.modified && (
            <CardRowSimple label={"Date modified"} value={formatDateTimeFromISO(genome.modified, { hideTime: true })} />
          )}
          <UserGroupsCardRow userGroups={genome.userGroups} />
          {isSyntenySet(genome.sequencingType) && (
            <CardRowSimple label={"Synteny Viewer"}>
              <SyntenyViewTile
                title={"Open in Synteny Viewer"}
                to={`/visualizer/synteny/${genome.id}?${viewid ? "viewid=" + viewid : ""}`}
              />
            </CardRowSimple>
          )}
        </>
      )}
    </Card>
  );
};

export { GenomeCard };

export interface IUserGroupsCardRowProps {
  userGroups: UserGroup[];
}

export const UserGroupsCardRow: FC<IUserGroupsCardRowProps> = (props) => {
  return props.userGroups && props.userGroups.length > 0 ? (
    <CardRowSimple label={"User groups"}>
      <div className={style.tags_list}>
        {props.userGroups.map((ug) => (
          <Tag key={ug.name} label={ug.name} intentColor={TagIntent.Secondary} />
        ))}
      </div>
    </CardRowSimple>
  ) : null;
};
